import type Uppy from '@uppy/core';
import type { Body, Meta } from '@uppy/core';
import type Dashboard from '@uppy/dashboard';

/**
 * Uppy emits `complete` once per upload batch, and files added while another upload is running
 * ("+ Add more") get their own batch. Resetting Uppy on an early batch's `complete` drops the batches
 * that are still uploading, so callers must wait until this returns false.
 */
export const hasUploadsInProgress = (uppy: Uppy<Meta, Body>): boolean => (
  Object.keys(uppy.getState().currentUploads ?? {}).length > 0
);

/**
 * Files added while a batch is running or paused belong to no batch until something calls `upload()` again:
 * `resumeAll()` only resumes files that already started, and `clear()` silently drops these.
 */
export const getUnqueuedFileIDs = (uppy: Uppy<Meta, Body>): string[] => {
  const { currentUploads, files } = uppy.getState();
  const queuedFileIDs = new Set(Object.values(currentUploads ?? {}).flatMap((upload) => upload.fileIDs));
  return Object.values(files ?? {})
    .filter((file) => !file.progress?.uploadStarted && !queuedFileIDs.has(file.id))
    .map((file) => file.id);
};

interface FinishUploadSessionOptions {
  closeModal: boolean;
  /** Tears the instance down instead of clearing it; pass the component's guarded destroy. */
  destroy?: () => void;
}

/**
 * Resets Uppy once every batch has finished. Failed files are kept in the Dashboard (with the modal open)
 * so the user sees the error and can retry, instead of the file silently disappearing.
 */
export const finishUploadSession = (
  uppy: Uppy<Meta, Body>,
  { closeModal, destroy }: FinishUploadSessionOptions,
): void => {
  const files = uppy.getFiles();
  if (files.some((file) => file.error)) {
    uppy.removeFiles(files.filter((file) => !file.error).map((file) => file.id));
    return;
  }

  if (closeModal) {
    uppy.getPlugin<Dashboard<Meta, Body>>('Dashboard')?.closeModal();
  }

  if (destroy) {
    destroy();
  } else {
    uppy.clear();
  }
};
